import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors } from "@/lib/theme";

interface HtmlContentProps {
    html: string;
}

/**
 * Lightweight HTML-to-RN renderer for Welcome Package static content.
 * Handles: h2, h3, p, ul/ol with li, strong/b, br, table (simple).
 * Not a full HTML parser — just enough for our structured content blocks.
 */
export function HtmlContent({ html }: HtmlContentProps) {
    const nodes = parseHtml(html);
    return (
        <View>
            {nodes.map((node, i) => (
                <HtmlNode key={i} node={node} />
            ))}
        </View>
    );
}

// Minimal DOM-like node structure
interface ParsedNode {
    tag: string;
    children: (ParsedNode | string)[];
}

function parseHtml(html: string): ParsedNode[] {
    // Strip whitespace between tags
    const cleaned = html.trim().replace(/>\s+</g, "><");
    const nodes: ParsedNode[] = [];
    let pos = 0;

    while (pos < cleaned.length) {
        if (cleaned[pos] === "<") {
            const closeTag = cleaned.indexOf(">", pos);
            if (closeTag === -1) {
                break;
            }

            const tagContent = cleaned.slice(pos + 1, closeTag);

            // Skip closing tags at top level
            if (tagContent.startsWith("/")) {
                pos = closeTag + 1;
                continue;
            }

            // Self-closing tags
            if (tagContent === "br" || tagContent === "br/") {
                nodes.push({ tag: "br", children: [] });
                pos = closeTag + 1;
                continue;
            }

            const tagName = tagContent.split(/[\s/]/)[0].toLowerCase();
            const endTag = `</${tagName}>`;
            const endPos = findMatchingClose(cleaned, tagName, closeTag + 1);

            if (endPos !== -1) {
                const inner = cleaned.slice(closeTag + 1, endPos);
                nodes.push({
                    tag: tagName,
                    children: isBlockTag(tagName) ? parseHtml(inner) : parseInline(inner)
                });
                pos = endPos + endTag.length;
            } else {
                pos = closeTag + 1;
            }
        } else {
            const nextTag = cleaned.indexOf("<", pos);
            const text = cleaned.slice(pos, nextTag === -1 ? undefined : nextTag);
            if (text.trim()) {
                nodes.push({ tag: "text", children: [decodeEntities(text)] });
            }
            pos = nextTag === -1 ? cleaned.length : nextTag;
        }
    }

    return nodes;
}

function findMatchingClose(html: string, tag: string, startPos: number): number {
    let depth = 1;
    let pos = startPos;
    const openTag = `<${tag}`;
    const closeTag = `</${tag}>`;

    while (pos < html.length && depth > 0) {
        const nextOpen = html.indexOf(openTag, pos);
        const nextClose = html.indexOf(closeTag, pos);

        if (nextClose === -1) {
            return -1;
        }

        if (nextOpen !== -1 && nextOpen < nextClose) {
            // Check it's actually an open tag (not <table vs <td)
            const charAfter = html[nextOpen + openTag.length];
            if (charAfter === ">" || charAfter === " " || charAfter === "/") {
                depth++;
            }
            pos = nextOpen + 1;
        } else {
            depth--;
            if (depth === 0) {
                return nextClose;
            }
            pos = nextClose + 1;
        }
    }
    return -1;
}

function isBlockTag(tag: string): boolean {
    return ["div", "ul", "ol", "table", "thead", "tbody", "tr"].includes(tag);
}

function parseInline(html: string): (ParsedNode | string)[] {
    const result: (ParsedNode | string)[] = [];
    let pos = 0;

    while (pos < html.length) {
        if (html[pos] === "<") {
            const closeAngle = html.indexOf(">", pos);
            if (closeAngle === -1) {
                break;
            }

            const tagContent = html.slice(pos + 1, closeAngle);

            if (tagContent === "br" || tagContent === "br/") {
                result.push({ tag: "br", children: [] });
                pos = closeAngle + 1;
                continue;
            }

            if (tagContent.startsWith("/")) {
                pos = closeAngle + 1;
                continue;
            }

            const tagName = tagContent.split(/[\s/]/)[0].toLowerCase();

            if (isBlockTag(tagName)) {
                const endTag = `</${tagName}>`;
                const endPos = findMatchingClose(html, tagName, closeAngle + 1);
                if (endPos !== -1) {
                    const inner = html.slice(closeAngle + 1, endPos);
                    result.push({ tag: tagName, children: parseHtml(inner) });
                    pos = endPos + endTag.length;
                } else {
                    pos = closeAngle + 1;
                }
                continue;
            }

            const endTag = `</${tagName}>`;
            const endPos = html.indexOf(endTag, closeAngle);
            if (endPos !== -1) {
                const inner = html.slice(closeAngle + 1, endPos);
                result.push({ tag: tagName, children: parseInline(inner) });
                pos = endPos + endTag.length;
            } else {
                pos = closeAngle + 1;
            }
        } else {
            const nextTag = html.indexOf("<", pos);
            const text = html.slice(pos, nextTag === -1 ? undefined : nextTag);
            if (text) {
                result.push(decodeEntities(text));
            }
            pos = nextTag === -1 ? html.length : nextTag;
        }
    }

    return result;
}

function decodeEntities(text: string): string {
    return text
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ");
}

// ─── Render ────────────────────────────────────────────────

function HtmlNode({ node }: { node: ParsedNode | string }) {
    if (typeof node === "string") {
        return <Text style={styles.bodyText}>{node}</Text>;
    }

    switch (node.tag) {
        case "h2":
            return (
                <Text style={styles.h2}>
                    <InlineChildren children={node.children} />
                </Text>
            );
        case "h3":
            return (
                <Text style={styles.h3}>
                    <InlineChildren children={node.children} />
                </Text>
            );
        case "p":
            return (
                <Text style={styles.paragraph}>
                    <InlineChildren children={node.children} />
                </Text>
            );
        case "ul":
            return (
                <View style={styles.list}>
                    {node.children
                        .filter((c): c is ParsedNode => typeof c !== "string" && c.tag === "li")
                        .map((li, i) => (
                            <View key={i} style={styles.listItem}>
                                <Text style={styles.bullet}>{"\u2022"}</Text>
                                <Text style={styles.listText}>
                                    <InlineChildren children={li.children} />
                                </Text>
                            </View>
                        ))}
                </View>
            );
        case "ol":
            return (
                <View style={styles.list}>
                    {node.children
                        .filter((c): c is ParsedNode => typeof c !== "string" && c.tag === "li")
                        .map((li, i) => (
                            <View key={i} style={styles.listItem}>
                                <Text style={styles.olNumber}>{i + 1}.</Text>
                                <Text style={styles.listText}>
                                    <InlineChildren children={li.children} />
                                </Text>
                            </View>
                        ))}
                </View>
            );
        case "table":
            return (
                <View style={styles.table}>
                    {node.children.map((child, i) => (
                        <HtmlNode key={i} node={child} />
                    ))}
                </View>
            );
        case "tr":
            return (
                <View style={styles.tableRow}>
                    {node.children
                        .filter(
                            (c): c is ParsedNode =>
                                typeof c !== "string" && (c.tag === "td" || c.tag === "th")
                        )
                        .map((cell, i) => (
                            <View
                                key={i}
                                style={[
                                    styles.tableCell,
                                    cell.tag === "th" && styles.tableHeaderCell
                                ]}
                            >
                                <Text
                                    style={
                                        cell.tag === "th" ? styles.tableHeaderText : styles.bodyText
                                    }
                                >
                                    <InlineChildren children={cell.children} />
                                </Text>
                            </View>
                        ))}
                </View>
            );
        case "br":
            return <Text>{"\n"}</Text>;
        case "strong":
        case "b":
            return (
                <Text style={styles.bold}>
                    <InlineChildren children={node.children} />
                </Text>
            );
        default:
            return (
                <View>
                    {node.children.map((child, i) => (
                        <HtmlNode key={i} node={child} />
                    ))}
                </View>
            );
    }
}

function InlineChildren({ children }: { children: (ParsedNode | string)[] }) {
    return (
        <>
            {children.map((child, i) => {
                if (typeof child === "string") {
                    return child;
                }
                if (child.tag === "strong" || child.tag === "b") {
                    return (
                        <Text key={i} style={styles.bold}>
                            <InlineChildren children={child.children} />
                        </Text>
                    );
                }
                if (child.tag === "br") {
                    return "\n";
                }
                // For any other inline tag, just render children
                return <InlineChildren key={i} children={child.children} />;
            })}
        </>
    );
}

const styles = StyleSheet.create({
    h2: {
        fontSize: 18,
        fontWeight: "700",
        color: colors.textPrimary,
        marginBottom: 8
    },
    h3: {
        fontSize: 15,
        fontWeight: "700",
        color: colors.textPrimary,
        marginTop: 12,
        marginBottom: 6
    },
    paragraph: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.textPrimary,
        marginBottom: 8
    },
    bodyText: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.textPrimary
    },
    bold: {
        fontWeight: "700"
    },
    list: {
        marginBottom: 8,
        paddingLeft: 4
    },
    listItem: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 4,
        paddingRight: 16
    },
    bullet: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.textSecondary,
        width: 16
    },
    olNumber: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.textSecondary,
        width: 24,
        fontWeight: "600"
    },
    listText: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.textPrimary,
        flex: 1
    },
    table: {
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 8
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: colors.surfaceBorder
    },
    tableCell: {
        flex: 1,
        padding: 8
    },
    tableHeaderCell: {
        backgroundColor: colors.backgroundSecondary
    },
    tableHeaderText: {
        fontSize: 13,
        fontWeight: "700",
        color: colors.textPrimary
    }
});
